'use client';

import { FormattedMessage, useIntl } from 'react-intl';
import logGuideClicked from '@alltrails/amplitude/events/logGuideClicked';
import logGuidesSwiped from '@alltrails/amplitude/events/logGuidesSwiped';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import Carousel from '@alltrails/denali/components/Carousel';
import { GuideCard } from '@alltrails/cards';
import { GuideHit } from '@alltrails/search/types/algoliaResultTypes';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { useCallback, useMemo, useRef, type JSX } from 'react';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { maxSlideWidth, slidesPerViewBreakpoints, slidesPerViewMobile } from '../carouselConstants';
import guidesIndexSrc from './guidesIndex.avif';
import styles from './styles/GuidesCarousel.module.scss';
import { LocationDetailsResponseType } from '@/types/locationDetailsPages/shared';
import { getAnalyticsPageType } from '@/utils/getAnalyticsPageType';
import { getGuideSourceType } from '@/utils/getGuideSourceType';

type GuidesCarouselProps = {
  guides: GuideHit[];
  parkId?: number;
  type: LocationDetailsResponseType;
};

const GuidesCarousel = ({ guides, parkId, type }: GuidesCarouselProps): JSX.Element => {
  const pageType = getAnalyticsPageType(type);
  const sourceType = getGuideSourceType(type);
  const languageRegionCode = useLanguageRegionCode();
  const intl = useIntl();
  const items = [...guides, { objectID: 'index' } as GuideHit];
  const carousel = useRef(false);
  const indexTitle = intl.formatMessage({ defaultMessage: 'Explore more national park guides' });
  const isMobileSizedScreen = useIsMobileSizedScreen();

  const handleGuideCardClicked = useCallback(
    (id: number | string, index: number) => {
      logGuideClicked({
        guide_id: String(id),
        horizontal_index: index + 1,
        source_type: sourceType
      });
    },
    [sourceType]
  );

  const indexGuideCard = useMemo(
    () => (
      <GuideCard
        href={wrapUrlSafe('/guides/country', languageRegionCode)}
        target="_blank"
        onClick={() => handleGuideCardClicked('explore more national park guides', items.length - 1)}
        photoUrl={guidesIndexSrc}
        title={`<em>${indexTitle}</em>`}
        altText={indexTitle}
        maxWidth={maxSlideWidth}
      />
    ),
    [handleGuideCardClicked, indexTitle, items.length, languageRegionCode]
  );
  const ref = useRef<HTMLDivElement>(null);
  useLogWebSectionViewed(ref, 'Guides', pageType, { threshold: 1 });

  return (
    <div className={styles.guidesContent} ref={ref}>
      <div className={styles.header}>
        <PlusLogoBadge variant="full" size={isMobileSizedScreen ? 'md' : 'lg'} />
        <div className={styles.title}>
          <h2 className={styles.titleText}>
            <FormattedMessage defaultMessage="Guides" />
          </h2>
        </div>
      </div>
      <Carousel
        uniqueId="park-page-guides-carousel"
        showPaginationDots={false}
        items={items}
        renderItem={(guide, index) =>
          guide.objectID === 'index' ? (
            indexGuideCard
          ) : (
            <GuideCard
              href={wrapUrlSafe(`/${guide.slug}`, languageRegionCode)}
              target="_blank"
              onClick={() => handleGuideCardClicked(guide.ID, index)}
              photoUrl={`${guide?.photo_url}?size=1120w746h`}
              title={guide?.name_with_formatting || guide?.name}
              trailCount={guide?.trail_count || 0}
              maxWidth={maxSlideWidth}
            />
          )
        }
        getItemKey={guide => guide?.objectID}
        slidesPerViewMobile={slidesPerViewMobile}
        slidesPerViewBreakpoints={slidesPerViewBreakpoints}
        onSlideChange={() => {
          if (!carousel.current && parkId) {
            logGuidesSwiped({ park_id: String(parkId), source_type: 'location page' });
            carousel.current = true;
          }
        }}
      />
    </div>
  );
};

export default GuidesCarousel;
